import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts-pro";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

type WeeksSinceObsOmniCountRecord_T = {
  weeks_since_observation: number;
  omni_count: number;
};

const WeeksSinceObsOmniCountChart: React.FC<any> = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<WeeksSinceObsOmniCountRecord_T[]>([]);
  const [chartData, setChartData] = useState<{ x: string; y: number | null }[]>(
    []
  );

  const { showError } = useError();
  const { apiRequest } = useApi();

  const fetchWeeksSinceObsOmniCount = async () => {
    try {
      setLoading(true);
      const response = await apiRequest<WeeksSinceObsOmniCountRecord_T[]>(
        `/brand_reports/insights/weeks_since_obs_omni_count`,
        { method: "GET" }
      );
      if (response.data) setData(response.data);
    } catch (error) {
      console.error(error);
      showError(`Error fetching weeks since observation omni count.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeeksSinceObsOmniCount();
  }, []);

  useEffect(() => {
    const chartData = data.map((item) => ({
      x: `Week ${item.weeks_since_observation}`,
      y: item.omni_count ?? null,
    }));
    setChartData(chartData);
  }, [data]);

  const renderChart = () => {
    if (loading) {
      return <CircularProgress />;
    }

    if (!chartData || chartData.length === 0) {
      return <Typography>No data available for this selection</Typography>;
    }

    const totalCount = chartData.reduce((sum, item) => sum + (item.y ?? 0), 0);

    return (
      <BarChart
        dataset={chartData}
        slotProps={{
          legend: { hidden: true },
        }}
        xAxis={[
          {
            scaleType: "band",
            dataKey: "x",
            tickPlacement: "middle",
          },
        ]}
        series={[
          {
            dataKey: "y",
            label: (location: "legend" | "tooltip") => {
              return "";
            },
            valueFormatter: (value: number | null) => {
              if (value === null) {
                return "";
              }
              const percentage =
                totalCount > 0 ? ((value / totalCount) * 100).toFixed(1) : "0";
              return `${percentage}%`;
            },
          },
        ]}
        borderRadius={9}
        height={500}
      />
    );
  };

  return (
    <Box>
      <Card>
        <CardContent>{renderChart()}</CardContent>
      </Card>
    </Box>
  );
};

export default WeeksSinceObsOmniCountChart;
